import { useRef, useEffect, useState, useCallback, useMemo } from "react";
import { Pose } from "@mediapipe/pose";
import { drawConnectors, drawLandmarks } from "@mediapipe/drawing_utils";
import { useNavigate, useLocation } from "react-router-dom";
import {
  FaPlayCircle,
  FaSyncAlt,
  FaSave,
  FaHistory,
  FaTimesCircle,
  FaExchangeAlt,
} from "react-icons/fa";
import { requestGetEvaluationHistory,
  requestSaveEvaluation,
  requestDeleteEvaluation,
  requestGetStudentById, auth } from "../utils/resource";
import { BodyVisualizer, EXERCISES } from "../utils/evaluationUtils";
import "../styles/StudentEvaluation.css";
import HistoryModal from "./HistoryModal";

const KNEE_ANGLE_THRESHOLD = 110;

function calculateAngle(a, b, c) {
  if (
    !a ||
    !b ||
    !c ||
    a.visibility < 0.5 ||
    b.visibility < 0.5 ||
    c.visibility < 0.5
  )
    return 0;
  const radians =
    Math.atan2(c.y - b.y, c.x - b.x) - Math.atan2(a.y - b.y, a.x - b.x);
  let angle = Math.abs((radians * 180.0) / Math.PI);
  if (angle > 180.0) angle = 360 - angle;
  return Math.round(angle);
}

function calculateAngles(landmarks, joints) {
  return joints.map(([a, b, c]) =>
    calculateAngle(landmarks[a], landmarks[b], landmarks[c])
  );
}

const StudentEvaluation = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { studentId, level } = location.state || {};

  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const poseRef = useRef(null);
  const onResultsCallback = useRef(null);
  const animationFrameId = useRef(null);

  const [currentExercise, setCurrentExercise] = useState(0);
  const [minAngles, setMinAngles] = useState([]);
  const [bestPoseCapture, setBestPoseCapture] = useState([]);
  const [bestPoseJoints, setBestPoseJoints] = useState([]);
  const [kneeAngles, setKneeAngles] = useState({ right: 0, left: 0 });
  const [evaluationStatus, setEvaluationStatus] = useState("idle"); // idle, ready, calibrating, invalid_pose
  const [showHistory, setShowHistory] = useState(false);
  const [facingMode, setFacingMode] = useState("user");
  const [history, setHistory] = useState([]);
  const [studentName, setStudentName] = useState("");

  const currentExerciseData = useMemo(
    () => EXERCISES[currentExercise],
    [currentExercise]
  );

  useEffect(() => {
    if (!auth?.currentUser?.email) {
      navigate("/");
      return;
    }
    if (!studentId || !level) {
      navigate("/student-selector");
      return;
    }

    const fetchStudentName = async () => {
      const result = await requestGetStudentById(studentId);
      if (result.status === "success") setStudentName(result.data.name);
      else console.error("Erro ao carregar nome do aluno:", result.message);
    };
    fetchStudentName();

    const fetchHistory = async () => {
      const result = await requestGetEvaluationHistory(studentId);
      if (result.status === "success") {
        setHistory(
          result.data.sort((a, b) => new Date(a.date) - new Date(b.date))
        );
      } else console.error("Erro ao carregar histórico:", result.message);
    };
    fetchHistory();
  }, [navigate, studentId, level]);

  const stopCameraAndTracks = useCallback(() => {
    if (animationFrameId.current) {
      window.cancelAnimationFrame(animationFrameId.current);
      animationFrameId.current = null;
    }
    if (videoRef.current?.srcObject) {
      videoRef.current.srcObject.getTracks().forEach((track) => track.stop());
      videoRef.current.srcObject = null;
    }
    poseRef.current?.close();
    poseRef.current = null;
  }, []);

  const processFrame = useCallback(async () => {
    if (videoRef.current && poseRef.current) {
      await poseRef.current.send({ image: videoRef.current });
      animationFrameId.current = window.requestAnimationFrame(processFrame);
    }
  }, []);

  const setupCamera = useCallback(
    async (mode) => {
      stopCameraAndTracks();
      poseRef.current = new Pose({
        locateFile: (file) =>
          `https://cdn.jsdelivr.net/npm/@mediapipe/pose/${file}`,
      });
      poseRef.current.setOptions({
        modelComplexity: 1,
        smoothLandmarks: true,
        minDetectionConfidence: 0.5,
        minTrackingConfidence: 0.5,
      });
      poseRef.current.onResults(
        (results) =>
          onResultsCallback.current && onResultsCallback.current(results)
      );

      if (videoRef.current) {
        try {
          const stream = await navigator.mediaDevices.getUserMedia({
            video: {
              width: { ideal: 1280 },
              height: { ideal: 720 },
              facingMode: mode,
            },
            audio: false,
          });
          videoRef.current.srcObject = stream;
          videoRef.current.onloadedmetadata = () =>
            videoRef.current.play().then(processFrame);
        } catch (error) {
          console.error("Erro ao tentar a câmera:", error);
          alert(
            "Não foi possível acessar a câmera. Tente novamente mais tarde."
          );
        }
      }
    },
    [stopCameraAndTracks, processFrame]
  );

  useEffect(() => {
    setupCamera(facingMode);
    return () => stopCameraAndTracks();
  }, [facingMode, setupCamera, stopCameraAndTracks]);

  const handleOnResults = useCallback(
    (results) => {
      const canvas = canvasRef.current;
      if (!canvas) return;
      const ctx = canvas.getContext("2d");
      canvas.width = videoRef.current.videoWidth;
      canvas.height = videoRef.current.videoHeight;
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(results.image, 0, 0, canvas.width, canvas.height);

      if (!results.poseLandmarks) {
        setEvaluationStatus("idle");
        return;
      }

      const lm = results.poseLandmarks;
      const rightKneeAngle = calculateAngle(lm[24], lm[26], lm[28]);
      const leftKneeAngle = calculateAngle(lm[23], lm[25], lm[27]);
      setKneeAngles({ right: rightKneeAngle, left: leftKneeAngle });

      if (!currentExerciseData.validation.positionCheck(lm)) {
        setEvaluationStatus("idle");
        drawConnectors(ctx, lm, Pose.POSE_CONNECTIONS, {
          color: "#FFFFFF",
          lineWidth: 2,
        });
        drawLandmarks(ctx, lm, { color: "#CCCCCC", lineWidth: 1, radius: 3 });
        return;
      }

      if (evaluationStatus === "idle") setEvaluationStatus("ready");

      if (evaluationStatus === "calibrating") {
        const isKneeBent =
          (currentExerciseData.validation.kneesToCheck.includes("right") &&
            rightKneeAngle > 0 &&
            rightKneeAngle < KNEE_ANGLE_THRESHOLD) ||
          (currentExerciseData.validation.kneesToCheck.includes("left") &&
            leftKneeAngle > 0 &&
            leftKneeAngle < KNEE_ANGLE_THRESHOLD);

        if (isKneeBent) {
          setEvaluationStatus("invalid_pose");
          drawConnectors(ctx, lm, Pose.POSE_CONNECTIONS, {
            color: "#FF0000",
            lineWidth: 3,
          });
          drawLandmarks(ctx, lm, { color: "#FF0000", radius: 4 });
          return;
        }

        drawConnectors(ctx, lm, Pose.POSE_CONNECTIONS, {
          color: "#00FF00",
          lineWidth: 3,
        });
        drawLandmarks(ctx, lm, { color: "#FF0000", radius: 4 });

        // Lógica de captura do melhor ângulo
        const angles = calculateAngles(lm, currentExerciseData.joints);
        if (currentExerciseData.resultMode === "single") {
          const validAngles = angles.filter((angle) => angle > 0);
          if (validAngles.length === 0) return;
          const bestCurrentAngle = Math.min(...validAngles);
          const bestAngleIndex = angles.indexOf(bestCurrentAngle);
          setMinAngles((prev) => {
            if (bestCurrentAngle < prev[0]) {
              setBestPoseCapture([lm]);
              setBestPoseJoints([currentExerciseData.joints[bestAngleIndex]]);
              return [bestCurrentAngle];
            }
            return prev;
          });
        } else {
          setMinAngles((prev) => {
            if (prev.length === 0) {
              setBestPoseCapture(angles.map(() => lm));
              return angles;
            }
            const newAngles = angles.map((a, i) =>
              a === 0 ? prev[i] : Math.min(a, prev[i])
            );
            setBestPoseCapture((prevBestPoses) => {
              const newBestPoses = [...prevBestPoses];
              newAngles.forEach((angle, i) => {
                if (angle < prev[i] && angles[i] !== 0) newBestPoses[i] = lm;
              });
              return newBestPoses;
            });
            return newAngles;
          });
        }
      } else {
        // idle, ready, invalid_pose
        const color =
          evaluationStatus === "invalid_pose" ? "#FF0000" : "#FFFFFF";
        drawConnectors(ctx, lm, Pose.POSE_CONNECTIONS, {
          color: color,
          lineWidth: 2,
        });
        drawLandmarks(ctx, lm, { color: color, lineWidth: 1, radius: 3 });
      }
    },
    [currentExerciseData, evaluationStatus]
  );

  onResultsCallback.current = handleOnResults;

  const handleReset = useCallback(() => {
    setEvaluationStatus(evaluationStatus === "idle" ? "idle" : "ready");
    setMinAngles([]);
    setBestPoseCapture([]);
    setBestPoseJoints([]);
  }, [evaluationStatus]);

  const handleStart = useCallback(() => {
    if (evaluationStatus !== "ready") return;
    const numResults =
      currentExerciseData.resultMode === "single"
        ? 1
        : currentExerciseData.joints.length;
    setMinAngles(Array(numResults).fill(180));
    setBestPoseCapture(Array(numResults).fill(null));
    setBestPoseJoints(
      currentExerciseData.resultMode === "multi"
        ? currentExerciseData.joints
        : Array(numResults).fill(null)
    );
    setEvaluationStatus("calibrating");
  }, [evaluationStatus, currentExerciseData]);

  const handleSave = useCallback(async () => {
    if (evaluationStatus === "invalid_pose") {
      alert("Não é possível salvar. O joelho foi dobrado durante a avaliação.");
      return;
    }
    if (minAngles.length === 0 || minAngles.some((angle) => angle === 180)) {
      alert("Inicie e realize o exercício completamente para poder salvar.");
      return;
    }
    const posesAsObject = bestPoseCapture.reduce((acc, pose, index) => {
      if (pose) acc[`pose_${index}`] = pose;
      return acc;
    }, {});
    const jointsToSave = bestPoseJoints.map((jointSet) => ({
      points: jointSet,
    }));
    const entry = {
      date: new Date().toISOString(),
      exercise: currentExerciseData.name,
      angles: minAngles.map((a) => Math.round(a)),
      poses: posesAsObject,
      joints: jointsToSave,
      level: level,
    };
    try {
      const result = await requestSaveEvaluation(studentId, entry);
      if (result.status === "success") {
        setHistory((prev) =>
          [...prev, { ...entry, id: result.id }].sort(
            (a, b) => new Date(a.date) - new Date(b.date)
          )
        );
        handleReset();
        alert("Avaliação salva com sucesso!");
      } else alert("Erro ao salvar a avaliação. Tente novamente.");
    } catch (error) {
      console.error("Erro ao salvar a avaliação:", error);
      alert("Erro ao salvar a avaliação. Tente novamente.");
    }
  }, [
    studentId,
    level,
    minAngles,
    bestPoseCapture,
    bestPoseJoints,
    currentExerciseData,
    evaluationStatus,
    handleReset,
  ]);

  const handleDeleteEvaluation = useCallback(
    async (evaluationId) => {
      if (window.confirm("Tem certeza que deseja deletar esta avaliação?")) {
        try {
          const result = await requestDeleteEvaluation(studentId, evaluationId);
          if (result.status === "success") {
            setHistory(history.filter((item) => item.id !== evaluationId));
            alert("Avaliação deletada com sucesso!");
          } else alert("Erro ao deletar a avaliação. Tente novamente.");
        } catch (error) {
          console.error("Erro ao deletar a avaliação:", error);
          alert("Erro ao deletar a avaliação.");
        }
      }
    },
    [studentId, history]
  );

  const handleCancel = useCallback(() => {
    stopCameraAndTracks();
    navigate("/student-selector");
  }, [navigate, stopCameraAndTracks]);

  const handleSwitchCamera = useCallback(() => {
    setFacingMode((prevMode) => (prevMode === "user" ? "environment" : "user"));
  }, []);

  const handleExerciseChange = useCallback(
    (e) => {
      setCurrentExercise(Number(e.target.value));
      handleReset();
    },
    [handleReset]
  );

  const saveDisabled =
    evaluationStatus !== "calibrating" || minAngles.some((a) => a === 180);

  return (
    <div className="s-e-app-container">
      <div className="s-e-camera-container">
        <video
          ref={videoRef}
          style={{ display: "none" }}
          playsInline
          autoPlay
        />
        <canvas ref={canvasRef} />
      </div>
      <div className="s-e-overlay-container">
        <div className="s-e-top-bar">
          {studentName && (
            <p>
              Aluno(a): <b>{studentName}</b>
            </p>
          )}
          <select value={currentExercise} onChange={handleExerciseChange}>
            {EXERCISES.map((ex, index) => (
              <option key={index} value={index}>
                {ex.name}
              </option>
            ))}
          </select>
          {(evaluationStatus === "idle" || evaluationStatus === "ready") && (
            <div className="s-e-instructions">
              <p>
                <b>Exercício:</b> {currentExerciseData.name}
              </p>
              <p style={{ whiteSpace: "pre-line" }}>
                <b>Instruções:</b> {currentExerciseData.instructions}
              </p>
            </div>
          )}
        </div>
        <div className="s-e-status-messages">
          {evaluationStatus === "idle" && (
            <p className="s-e-status-alert">Posicione o corpo na área</p>
          )}
          {evaluationStatus === "ready" && (
            <p className="s-e-status-ok">Pronto para iniciar!</p>
          )}
          {evaluationStatus === "invalid_pose" && (
            <p className="s-e-status-error">
              Postura inválida: Joelho dobrado!
            </p>
          )}
        </div>
        {evaluationStatus !== "idle" && (
          <div className="s-e-knee-angles-container">
            <p className={kneeAngles.right > 0 ? "" : "s-e-not-detected"}>
              Joelho Direito:{" "}
              <b>{kneeAngles.right > 0 ? `${kneeAngles.right}°` : "--"}</b>
            </p>
            <p className={kneeAngles.left > 0 ? "" : "s-e-not-detected"}>
              Joelho Esquerdo:{" "}
              <b>{kneeAngles.left > 0 ? `${kneeAngles.left}°` : "--"}</b>
            </p>
          </div>
        )}
        <div className="s-e-live-results">
          {(evaluationStatus === "calibrating" ||
            evaluationStatus === "invalid_pose") && <h3>Tentativa</h3>}
          {(evaluationStatus === "calibrating" ||
            evaluationStatus === "invalid_pose") && (
            <div className="s-e-miniatures-container">
              {minAngles.map((angle, idx) => {
                if (!bestPoseCapture[idx] || angle === 180) return null;
                const label =
                  currentExerciseData.resultMode === "multi"
                    ? idx === 0
                      ? "Lado Direito"
                      : "Lado Esquerdo"
                    : "";
                return (
                  <div key={idx} className="s-e-miniature-item">
                    {label && <b>{label}</b>}
                    <BodyVisualizer
                      pose={bestPoseCapture[idx]}
                      angleData={bestPoseJoints[idx]}
                      angleValue={angle}
                      width={100}
                      height={100}
                    />
                  </div>
                );
              })}
            </div>
          )}
        </div>
        <div className="s-e-bottom-bar">
          {evaluationStatus !== "calibrating" &&
          evaluationStatus !== "invalid_pose" ? (
            <button
              onClick={handleStart}
              disabled={evaluationStatus !== "ready"}
              className="s-e-btn-start"
              title="Iniciar Avaliação"
            >
              <FaPlayCircle />
            </button>
          ) : (
            <button
              onClick={handleReset}
              className="s-e-btn-reset"
              title="Reiniciar"
            >
              <FaSyncAlt />
            </button>
          )}
          <button
            onClick={handleSave}
            disabled={saveDisabled}
            className="s-e-btn-save"
            title="Salvar Avaliação"
          >
            <FaSave />
          </button>
          <button
            onClick={() => setShowHistory(true)}
            className="s-e-btn-history"
            title="Ver Histórico"
          >
            <FaHistory />
          </button>
          <button
            onClick={handleSwitchCamera}
            className="s-e-btn-switch-camera"
            title="Alternar Câmera"
          >
            <FaExchangeAlt />
          </button>
          <button
            onClick={handleCancel}
            className="s-e-btn-cancel"
            title="Cancelar e Sair"
          >
            <FaTimesCircle />
          </button>
        </div>
      </div>
      <HistoryModal
        show={showHistory}
        onClose={() => setShowHistory(false)}
        history={history}
        studentName={studentName}
        onDelete={handleDeleteEvaluation}
        exercises={EXERCISES}
      />
    </div>
  );
};

export default StudentEvaluation;
